import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Play, X, ShoppingCart, Headphones, Tv } from "lucide-react";
import { KineticText, Reveal } from "@/components/Motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

// NOTE: replace VIDEO IDs below with your real YouTube episode IDs
const YT = "NJWXU2M2Es4";      // Giving Em Something to Talk About
const YT2 = "qhAFMkr_Rc8";     // The Midweek Level Up
const YT3 = "V-LvbKdYbRk";     // Giving Em Something to Talk About (ep 2)
const YT4 = "dtytSoqSw4w";     // The Midweek Level Up (latest)
const thumb = (u) => u;

const SHOWS = [
  {
    key: "talk",
    icon: Headphones,
    tag: "The Podcast",
    title: "Giving Em Something to Talk About",
    desc: "Unfiltered, real conversations on love, life, growth and everything worth talking about.",
    featured: YT,
    episodes: [
      { id: YT, title: "Ep. 12 — Boundaries & Big Energy", len: "48 min", img: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHw0fHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODc3NzEzMzZ8MA&ixlib=rb-4.1.0&q=85" },
      { id: YT3, title: "Ep. 11 — The Hard Conversations", len: "52 min", img: "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODc3NzEzMzZ8MA&ixlib=rb-4.1.0&q=85" },
      { id: YT, title: "Ep. 10 — Love, Loyalty & Lies", len: "44 min", img: "https://images.unsplash.com/photo-1531651008558-ed1740375b39?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODc3NzEzMzZ8MA&ixlib=rb-4.1.0&q=85" },
    ],
  },
  {
    key: "levelup",
    icon: Tv,
    tag: "Weekly Show",
    title: "The Midweek Level Up",
    desc: "Your midweek reset — quick, high-energy episodes to keep you motivated and leveling up.",
    featured: YT4,
    episodes: [
      { id: YT4, title: "Wk. 24 — Reset Your Focus", len: "18 min", img: "https://images.unsplash.com/photo-1673767297353-0a4c8ad61b05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHw0fHxjb25maWRlbnQlMjBwZXJzb24lMjByZWNvcmRpbmclMjB2aWRlbyUyMGNvbnRlbnR8ZW58MHx8fHwxNzg3NzcxMzM2fDA&ixlib=rb-4.1.0&q=85" },
      { id: YT2, title: "Wk. 23 — Momentum > Motivation", len: "21 min", img: "https://images.unsplash.com/photo-1587050007609-d9686b217bd6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBwZXJzb24lMjByZWNvcmRpbmclMjB2aWRlbyUyMGNvbnRlbnR8ZW58MHx8fHwxNzg3NzcxMzM2fDA&ixlib=rb-4.1.0&q=85" },
      { id: YT2, title: "Wk. 22 — Show Up Anyway", len: "16 min", img: "https://images.unsplash.com/photo-1683203438853-f2105d43f559?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjb25maWRlbnQlMjBwZXJzb24lMjByZWNvcmRpbmclMjB2aWRlbyUyMGNvbnRlbnR8ZW58MHx8fHwxNzg3NzcxMzM2fDA&ixlib=rb-4.1.0&q=85" },
    ],
  },
];

const SHOW_EBOOKS = false; // flip to true when ebook files are ready to sell

const EBOOKS = [
  { title: "Something to Talk About", sub: "Real talk on love, life & everything between.", price: "$14.99", lookup: "ebook_talk", grad: "from-[#123B2C] to-[#2FD36A]" },
  { title: "The Level Up Playbook", sub: "52 midweek resets to keep you moving.", price: "$9.99", lookup: "ebook_playbook", grad: "from-[#0E2A44] to-[#1E7F8F]" },
  { title: "Unfiltered", sub: "Owning your story without apology.", price: "$12.99", lookup: "ebook_unfiltered", grad: "from-[#2A1140] to-[#7A3BD1]" },
  { title: "Speak Your Power", sub: "Find your voice, command the room.", price: "$11.99", lookup: "ebook_power", grad: "from-[#3A1220] to-[#D14B6B]" },
];

const VideoModal = ({ id, onClose }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    onClick={onClose} data-testid="video-modal"
    className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4">
    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-4xl">
      <div className="flex justify-end mb-3">
        <button onClick={onClose} data-testid="video-close" className="text-[#0E1E2E]/80 hover:text-brand"><X size={26} /></button>
      </div>
      <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
        <iframe className="absolute inset-0 w-full h-full border border-black/10"
          src={`https://www.youtube.com/embed/${id}?autoplay=1`} title="UMG Media episode"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
      </div>
    </div>
  </motion.div>
);

const Hero = ({ onPlay }) => (
  <section id="top" className="relative min-h-screen flex items-center pt-28 pb-16 px-6 md:px-12 overflow-hidden">
    <div className="absolute -top-40 -right-40 w-[38rem] h-[38rem] rounded-full bg-brand/10 blur-[120px] pointer-events-none" />
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full relative">
      <div className="lg:col-span-7">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="font-mono2 text-xs uppercase tracking-[0.28em] text-brand mb-6">
          Podcasts · Shows · New episodes weekly
        </motion.p>
        <h1 className="font-display font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-7xl lg:text-[7vw] text-[#0E1E2E]">
          <KineticText lines={["Your one-stop", "media hub."]} delay={0.15} />
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 text-[#556673] text-base md:text-lg leading-relaxed max-w-xl">
            Welcome to UMG Media — stream new episodes of
            <span className="text-[#0E1E2E]"> Giving Em Something to Talk About</span> and
            <span className="text-[#0E1E2E]"> The Midweek Level Up</span>.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href="#shows" data-testid="hero-watch"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand text-[#06111C] font-display font-bold uppercase tracking-wide text-sm hover:bg-[#27B95B] transition-colors">
              <Play size={16} fill="#06111C" /> Watch shows
            </a>
            {SHOW_EBOOKS && (
              <a href="#ebooks" data-testid="hero-shop"
                className="inline-flex items-center gap-2 px-8 py-4 border border-black/20 text-[#0E1E2E] font-display font-bold uppercase tracking-wide text-sm hover:border-brand hover:text-brand transition-colors">
                <ShoppingCart size={16} /> Shop ebooks
              </a>
            )}
          </div>
        </Reveal>
      </div>
      <div className="lg:col-span-5">
        <Reveal delay={0.4}>
          <button onClick={() => onPlay(YT)} data-testid="hero-featured"
            className="group relative w-full aspect-video overflow-hidden border border-black/10 bg-surface">
            <img src={thumb("https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxwb2RjYXN0JTIwcmVjb3JkaW5nJTIwc3R1ZGlvJTIwbWljcm9waG9uZXxlbnwwfHx8fDE3ODc3NzEzMzZ8MA&ixlib=rb-4.1.0&q=85")}
              alt="Latest episode" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A1420] via-transparent to-transparent" />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="w-16 h-16 rounded-full bg-brand flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                <Play size={26} fill="#06111C" className="ml-1" />
              </span>
            </span>
            <span className="absolute bottom-4 left-4 font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#0E1E2E]/90">Now Playing · Latest Episode</span>
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

const ShowBlock = ({ show, onPlay, index }) => (
  <div className="mb-20 last:mb-0">
    <Reveal className="flex items-center gap-3 mb-8">
      <show.icon size={18} className="text-brand" />
      <span className="font-mono2 text-xs uppercase tracking-[0.2em] text-brand">{show.tag}</span>
    </Reveal>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <Reveal className="lg:col-span-5" delay={0.05}>
        <h3 className="font-display font-black uppercase tracking-tight text-3xl md:text-4xl leading-tight text-[#0E1E2E]">{show.title}</h3>
        <p className="mt-4 text-[#556673] leading-relaxed">{show.desc}</p>
        <button onClick={() => onPlay(show.featured)} data-testid={`play-featured-${show.key}`}
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-brand text-[#06111C] font-display font-bold uppercase tracking-wide text-xs hover:bg-[#27B95B] transition-colors">
          <Play size={14} fill="#06111C" /> Play latest
        </button>
      </Reveal>
      <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {show.episodes.map((ep, i) => (
          <Reveal key={i} delay={i * 0.08}>
            <button onClick={() => onPlay(ep.id)} data-testid={`episode-${show.key}-${i}`}
              className="group text-left w-full">
              <div className="relative aspect-video overflow-hidden border border-black/10 bg-surface">
                <img src={ep.img} alt={ep.title} className="w-full h-full object-cover opacity-75 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-black/25" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-10 h-10 rounded-full bg-brand/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play size={16} fill="#06111C" className="ml-0.5" />
                  </span>
                </span>
                <span className="absolute bottom-2 right-2 font-mono2 text-[10px] bg-black/60 px-1.5 py-0.5 text-white">{ep.len}</span>
              </div>
              <p className="mt-2 text-sm text-[#3A4A56] group-hover:text-brand transition-colors leading-snug">{ep.title}</p>
            </button>
          </Reveal>
        ))}
      </div>
    </div>
  </div>
);

const EbookCard = ({ book, i, onBuy, paypal }) => (
  <Reveal delay={(i % 4) * 0.07}>
    <div className="group">
      <div className={`relative aspect-[3/4] overflow-hidden border border-black/10 bg-gradient-to-br ${book.grad} flex flex-col justify-between p-5`}>
        <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-white/70">UMG Media · Ebook</span>
        <div>
          <h3 className="font-display font-black uppercase tracking-tight text-2xl leading-none text-white">{book.title}</h3>
        </div>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </div>
      <div className="mt-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-sm text-[#3A4A56] leading-snug">{book.sub}</p>
          <p className="font-display font-bold text-brand mt-1">{book.price}</p>
        </div>
        <button onClick={() => onBuy(book)} data-testid={`buy-${i}`}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 bg-brand text-[#06111C] font-display font-bold uppercase tracking-wide text-xs hover:bg-[#27B95B] transition-colors">
          <ShoppingCart size={13} /> Buy
        </button>
      </div>
      {paypal && (
        <div className="mt-3" data-testid={`paypal-${i}`}>
          <PayPalButtons style={{ layout: "horizontal", height: 40, color: "gold", tagline: false }}
            createOrder={async () => { const { data } = await api.post("/paypal/create-order", { lookup_key: book.lookup, customer_email: book._email || null }); return data.order_id; }}
            onApprove={async (d) => { const { data: res } = await api.post("/paypal/capture-order", { order_id: d.orderID }); if (res.payment_status === "paid") { window.location.href = "/payment/success?session_id=" + d.orderID; } else { toast.error("PayPal payment not completed."); } }}
            onError={() => toast.error("PayPal error. Please try again.")} />
        </div>
      )}
    </div>
  </Reveal>
);

export default function Home() {
  const [video, setVideo] = useState(null);
  const [pp, setPp] = useState(null);
  const cust = useCustomerAuth();
  const custEmail = cust?.user?.email || null;

  useEffect(() => { api.get("/payments/config").then((r) => setPp(r.data)).catch(() => {}); }, []);

  const buyEbook = async (book) => {
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: book.lookup, origin_url: window.location.origin, customer_email: custEmail });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout. Please try again.");
    }
  };

  return (
    <div className="bg-[#F1F4F3]">
      <Navbar />
      <Hero onPlay={setVideo} />

      <div className="border-y border-black/10 py-4 bg-surface">
        <Marquee speed={32} gradient={false}>
          {["New Episodes Weekly", "Giving Em Something to Talk About", "The Midweek Level Up", "Real Conversations", "Stream Anywhere"].map((t, i) => (
            <span key={i} className="font-mono2 uppercase tracking-[0.26em] text-sm text-[#556673] mx-8">
              {t} <span className="text-brand mx-8">▶</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* Shows */}
      <section id="shows" className="px-6 md:px-12 py-24 md:py-32">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-brand mb-4">/ Watch & Listen</p>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl md:text-5xl mb-16 text-[#0E1E2E]">Our shows</h2>
        </Reveal>
        {SHOWS.map((s, i) => <ShowBlock key={s.key} show={s} onPlay={setVideo} index={i} />)}
      </section>

      {/* Ebooks */}
      {SHOW_EBOOKS && (
      <section id="ebooks" className="px-6 md:px-12 py-24 md:py-32 border-t border-black/10 bg-surface">
        <Reveal>
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-brand mb-4">/ The Bookshelf</p>
          <h2 className="font-display font-black uppercase tracking-tight text-3xl md:text-5xl mb-4 text-[#0E1E2E]">Order our ebooks</h2>
          <p className="text-[#556673] max-w-2xl mb-16">Digital reads to keep the conversation going and the momentum high. Instant download after purchase.</p>
        </Reveal>
        {pp?.paypal_enabled ? (
          <PayPalScriptProvider options={{ "client-id": pp.paypal_client_id, currency: "USD", intent: "capture" }}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {EBOOKS.map((b, i) => <EbookCard key={i} book={{ ...b, _email: custEmail }} i={i} onBuy={buyEbook} paypal />)}
            </div>
          </PayPalScriptProvider>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {EBOOKS.map((b, i) => <EbookCard key={i} book={b} i={i} onBuy={buyEbook} />)}
          </div>
        )}
      </section>
      )}

      {/* About */}
      <section id="about" className="px-6 md:px-12 py-24 md:py-32 border-t border-black/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          <Reveal className="md:col-span-4">
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-brand">/ About UMG Media</p>
          </Reveal>
          <Reveal className="md:col-span-8" delay={0.1}>
            <h2 className="font-display font-black uppercase tracking-tight text-3xl md:text-5xl leading-tight text-[#0E1E2E]">
              Stories, conversations & content worth pressing play on.
            </h2>
            <p className="mt-6 text-[#556673] text-base md:text-lg leading-relaxed max-w-3xl">
              UMG Media is your home for bold ideas and honest conversations across two original shows.
              Tune into the raw, honest talks of <span className="text-[#0E1E2E]">Giving Em Something to Talk About</span>,
              and get your midweek boost with <span className="text-[#0E1E2E]">The Midweek Level Up</span> — new episodes every week.
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
      <ChatWidget />
      {video && <VideoModal id={video} onClose={() => setVideo(null)} />}
    </div>
  );
}
